// src/components/team/TeamSubNav.jsx
//
// Team-page sub-navigation. Renders the role-aware tab bar that
// scopes the user inside a single team's context (Overview, Roster,
// Seasons, Schedule, Chat, etc.). Lives at the top of every
// /teams/:teamId route. The sidebar above it stays universal —
// global chrome only.
//
// Tab set adapts per the user's role on THIS team (via teamRole
// from RoleContext):
//   • Coach (owner/staff): Overview · Roster · Seasons · Schedule · Chat
//   • Parent              : Overview · Teammates · Schedule · Chat
//
// Mental model note (May-2026): Seasons is a first-class concept.
// A team has many seasons (Spring 2026, Fall 2026, …). Each season
// owns its own schedule, roster, registrations, attendance, and
// finances. Schedule stays in the nav for one-tap access to the
// active season's schedule (the most-common drilldown), but the
// Seasons tab is the explicit list view of all seasons for the
// team — including past and archived ones. Coaches who think
// "I want to look at last spring" now have a clear surface for it
// rather than having to know to navigate elsewhere.
//
// Mobile-first: horizontally scrollable, sticky at the top of the
// scroll container, lime underline on active tab.

import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRole } from '@/context/RoleContext';
import { useAuth } from '@/context/AuthContext';
import useChats from '@/hooks/useChats';
import {
  HomeIcon,
  UsersIcon,
  CalendarDaysIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

// Tab labels — bilingual since 2026-07-24 (the bar was the last English
// chrome on an otherwise-Spanish team page).
const TAB_STRINGS = {
  en: { overview: 'Overview', roster: 'Roster', teammates: 'Teammates', seasons: 'Seasons', schedule: 'Schedule', chat: 'Chat' },
  es: { overview: 'Resumen', roster: 'Plantel', teammates: 'Compañeros', seasons: 'Temporadas', schedule: 'Calendario', chat: 'Chat' },
};

export default function TeamSubNav({ teamId }) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const N = TAB_STRINGS[String(i18n?.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en'];
  const { teamRole } = useRole();
  const { firebaseUser } = useAuth();
  const { chats } = useChats(firebaseUser?.uid || null);

  if (!teamId) return null;

  const role = teamRole(teamId);
  // Tabs are role-aware — see the tab list below for label specifics.
  // Coaches get "Roster" (full admin); parents get "Teammates" (read-
  // only); users with no role on this team get neither.

  // Per-team unread indicator for the Chat tab. Looks at THIS team's
  // announcement channel only — the user's overall unread badge in
  // the sidebar / bottom-tab covers cross-team unread; this dot is
  // contextual ("there's something new in THIS team's chat right
  // now"). We use a simple lime dot rather than a count because the
  // announcement channel's isUnread is binary; mixing in DMs related
  // to this team would require schema work and isn't worth the noise.
  const teamAnnouncement = chats.find(
    (c) => c.type === 'team_announcement' && c.teamId === teamId
  );
  const chatHasUnread = !!teamAnnouncement?.isUnread;

  const tabs = [
    {
      to: `/teams/${teamId}`,
      label: N.overview,
      icon: HomeIcon,
      // Overview is the default — match exactly to avoid bleeding
      // active state onto sub-routes like /roster.
      end: true,
    },
    // Roster tab — same target URL for both roles, but the label flips
    // ("Roster" for coaches, "Teammates" for parents) to match the page
    // headers each role sees on /teams/:teamId/roster:
    //   - Coaches land on the admin RosterPage (full PII, edit/delete,
    //     bulk-invite). Header reads "Roster".
    //   - Parents land on the read-only ParentRosterView (firstName +
    //     lastName from playersPublic). Header reads "Teammates".
    // Pre-May-2026 the parent path was a Quick Link on the team
    // Overview only — no nav tab. Adding the tab gives parents a
    // consistent way to switch between Overview/Teammates/Schedule/
    // Chat without having to back out to Overview first.
    role === 'coach' ? {
      to: `/teams/${teamId}/roster`,
      label: N.roster,
      icon: UsersIcon,
    } : role === 'parent' ? {
      to: `/teams/${teamId}/roster`,
      label: N.teammates,
      icon: UsersIcon,
    } : null,
    // Seasons tab — coaches only. Lists every season this team has
    // (open / closed / archived) and is the entry point for picking
    // a non-active season to drill into. Sits between Roster and
    // Schedule because conceptually a season *contains* the schedule;
    // putting it before Schedule reinforces the parent→child relation.
    role === 'coach' ? {
      to: `/teams/${teamId}/seasons`,
      label: N.seasons,
      icon: CalendarDaysIcon,
      // Also light up when on a /seasons/:id (SeasonDetail) page that
      // belongs to this team — feels weird otherwise once they drill in.
      // Pathname check is conservative; SeasonDetail also renders the
      // sub-nav so the active state will read correctly there too.
      isActiveExtra: (p) => p === `/teams/${teamId}/seasons`,
    } : null,
    {
      to: `/teams/${teamId}/schedule`,
      label: N.schedule,
      // Schedule reads as "when things happen" — clock icon now that
      // CalendarDaysIcon is owned by the Seasons tab. Without the
      // swap both tabs would share the same icon and parents would
      // double-tap the wrong one on mobile.
      icon: ClockIcon,
      // Active for any path under /seasons/*/schedule too — they're
      // the same conceptual surface (team's schedule).
      isActiveExtra: (p) => p.endsWith('/schedule') && p.includes(teamId),
    },
    {
      to: `/teams/${teamId}/chat`,
      label: N.chat,
      icon: ChatBubbleLeftRightIcon,
      unread: chatHasUnread,
    },
  ].filter(Boolean);

  return (
    <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 mb-4 bg-[#f7faf5]/95 backdrop-blur-sm border-b border-zinc-200">
      <nav
        role="navigation"
        aria-label="Team sections"
        // On mobile: items stretch full width so 5 tabs distribute evenly
        // across the viewport (no horizontal scroll). On desktop: items
        // size to content with comfortable padding, scroll horizontally
        // on the rare case there are more tabs than the container fits.
        className="mx-auto max-w-5xl flex items-stretch sm:overflow-x-auto sm:scrollbar-hide"
      >
        {tabs.map((tab) => (
          <TabLink
            key={tab.to}
            tab={tab}
            location={location}
          />
        ))}
      </nav>
    </div>
  );
}

function TabLink({ tab, location }) {
  const Icon = tab.icon;
  const extraActive = tab.isActiveExtra
    ? tab.isActiveExtra(location.pathname)
    : false;

  return (
    <NavLink
      to={tab.to}
      end={!!tab.end}
      className={({ isActive }) => {
        const active = isActive || extraActive;
        return [
          // Mobile (<sm): icon-stacked-over-label, equal width via flex-1
          // so 5 tabs fit on iPhone SE (375px) without horizontal scroll.
          // Desktop (sm+): icon next to label, comfortable padding. Tap
          // target stays ≥44px in both modes.
          'relative flex flex-1 sm:flex-initial flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 px-2 sm:px-5 py-2 sm:py-3 min-h-[48px] sm:min-h-0 text-[10px] sm:text-sm font-bold uppercase tracking-[0.5px] whitespace-nowrap transition-colors',
          active
            ? 'text-[#0d1f14]'
            : 'text-zinc-500 hover:text-[#0d1f14]',
        ].join(' ');
      }}
      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
    >
      {({ isActive }) => {
        const active = isActive || extraActive;
        return (
          <>
            <span className="relative inline-flex items-center">
              {/* Icon size bumps up on mobile since it's now the primary
                  visual; on desktop it stays compact since the label
                  carries the load. */}
              <Icon className="h-5 w-5 sm:h-4 sm:w-4 shrink-0" />
              {/* Per-tab unread indicator — small lime dot pinned to
                  the top-right of the icon. Currently used only by
                  the Chat tab (announcement channel unread state).
                  Hidden once the user opens the chat (badge clears
                  via the markChatRead call in ChatRoomV2). */}
              {tab.unread && (
                <span
                  aria-label="Unread"
                  className="absolute -top-1 -right-1 inline-flex h-2 w-2 rounded-full bg-[#b5f03d] ring-2 ring-[#f7faf5]"
                />
              )}
            </span>
            <span className="leading-none sm:leading-normal">{tab.label}</span>
            {/* Active-state underline. Lime, full-width under the
                tab content. Sits flush with the bottom border of
                the bar so the look is "selected tab" not "highlighted
                button." */}
            {active && (
              <span
                aria-hidden="true"
                className="absolute left-2 right-2 bottom-0 h-[3px] rounded-t bg-[#b5f03d]"
              />
            )}
          </>
        );
      }}
    </NavLink>
  );
}
